import { useMemo } from 'react';
import sanitizeHtml from 'sanitize-html';
import { v4 as uuid } from 'uuid';

import { flowNodeColor } from 'fable/flowEditor';
import type { ChatExtraData } from 'fable/flowEditor/components/flowManager/canvas/types/extraData';
import { Text } from 'flow-builder/node/text';
import { TextBreak } from 'flow-builder/node/textBreak';
import { TextContent } from 'flow-builder/node/textContent';
import { hexStringToNumber } from 'flow-builder/renderers/common/colors';
import type {
  GenericNodeAttributes,
  GenericNodeProps,
} from 'flow-builder/types/node';

import ChatInputModal from '../../modals/chatinput';

export type ChatInputPayload = {
  field_label: string;
  field_placeholder: string;
  message: string;
  nextNodeId: string;
};

type ChatInputNodeAttributes = GenericNodeAttributes<
  ChatInputPayload,
  ChatExtraData
>;
type ChatInputNodeProps = GenericNodeProps<ChatInputPayload, ChatExtraData>;

const translatePayload: ChatInputNodeAttributes['translatePayload'] = (
  node,
) => {
  const { nextNodeId } = node.payload;

  return [
    {
      id: uuid(),
      label: 'arrow',
      connection: nextNodeId,
    },
  ];
};

const updateConnection: ChatInputNodeAttributes['updateConnection'] = (
  node,
  _connection,
  connectionId,
) => {
  const payload = { ...node.payload, nextNodeId: connectionId };

  return {
    ...node,
    payload,
  };
};

export const ChatInputNodeAttributes: ChatInputNodeAttributes = {
  iconName: 'input',
  label: 'Chat input',
  color: hexStringToNumber(flowNodeColor.orange),
  translatePayload,
  updateConnection,
  NodeContent: ChatInputNode,
  ActionModal: ChatInputModal,
};

export function ChatInputNode({ node }: ChatInputNodeProps): JSX.Element {
  const { payload } = node;
  const { message } = payload;

  const sanitizedHtmlMessage = sanitizeHtml(message, {
    /*
     * What's the point of having tags if canvas can't display them?
     */
    allowedTags: [],
  });

  return (
    <TextContent maxLines={2}>
      {useMemo(
        () => (
          <TextBreak>
            <Text weight="semibold" text={sanitizedHtmlMessage} />
          </TextBreak>
        ),
        [sanitizedHtmlMessage],
      )}
    </TextContent>
  );
}
